// Sugalarm — kiosk volume agent hook (cycle 22).
//
// Owns reachability + state for the loopback /volume agent. Surface:
//   - `available` flips to true once GET /volume succeeds; false on
//     network error / timeout / non-200, and on `variant === "web"`.
//   - `setLevel(n)` is debounced 150 ms so dragging the slider doesn't
//     flood the agent. Optimistic; reverts on error.
//   - `setMuted(b)` fires immediately.
//
// Variant-gated inside the hook (not at the call site) to keep Rules
// of Hooks happy.

import { useCallback, useEffect, useRef, useState } from "react";

import * as volumeApi from "../api/volume";
import type { VolumeState } from "../api/volume";
import type { FrameVariant } from "./useFrameVariant";

const DEBOUNCE_MS = 150;
// Cycle 34.12: cadence at which the hook re-probes the loopback
// agent while it's unreachable. The cycle-22 hook ran one probe on
// mount and gave up — so if the SPA loaded before the
// sugalarm-volumed.service finished booting (or the service hiccuped
// later in the session), the volume button stayed hidden forever
// until the operator reloaded the kiosk. Re-probing every 15 s makes
// it self-heal.
const REACHABILITY_POLL_MS = 15_000;

export interface VolumeAgentState {
  available: boolean;
  state: { level: number; muted: boolean } | null;
  setLevel: (n: number) => Promise<void>;
  setMuted: (m: boolean) => Promise<void>;
  refresh: () => Promise<void>;
}

export function useVolumeAgent(variant: FrameVariant): VolumeAgentState {
  const [available, setAvailable] = useState(false);
  const [state, setState] = useState<{ level: number; muted: boolean } | null>(
    null,
  );
  const debounceRef = useRef<number | null>(null);
  const pendingLevelRef = useRef<number | null>(null);
  const lastConfirmedRef = useRef<{ level: number; muted: boolean } | null>(null);

  const apply = useCallback((next: VolumeState) => {
    const slim = { level: next.level, muted: next.muted };
    setState(slim);
    lastConfirmedRef.current = slim;
    setAvailable(true);
  }, []);

  const refresh = useCallback(async () => {
    if (variant !== "kiosk") {
      setAvailable(false);
      setState(null);
      return;
    }
    const r = await volumeApi.getVolume();
    if (r.ok) {
      apply(r.data);
    } else {
      setAvailable(false);
      setState(null);
    }
  }, [variant, apply]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Cycle 34.12: keep poking the agent every 15 s while it's
  // unreachable so the volume button reappears as soon as the local
  // service comes back online — without a kiosk reload. Once the
  // agent answers (available flips true), the interval stops; if the
  // agent later disappears mid-session, the next setLevel / setMuted
  // call's error path flips available back to false and this
  // interval picks back up.
  useEffect(() => {
    if (variant !== "kiosk") return;
    if (available) return;
    const id = window.setInterval(() => {
      void refresh();
    }, REACHABILITY_POLL_MS);
    return () => window.clearInterval(id);
  }, [variant, available, refresh]);

  // Cleanup: cancel any pending debounced PUT on unmount.
  useEffect(() => {
    return () => {
      if (debounceRef.current != null) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, []);

  const setLevel = useCallback(
    async (n: number) => {
      const clamped = Math.max(0, Math.min(100, Math.round(n)));
      // Optimistic update — slider thumb tracks the finger smoothly even
      // before the agent confirms.
      setState((prev) =>
        prev ? { ...prev, level: clamped } : { level: clamped, muted: false },
      );
      pendingLevelRef.current = clamped;
      if (debounceRef.current != null) {
        window.clearTimeout(debounceRef.current);
      }
      await new Promise<void>((resolve) => {
        debounceRef.current = window.setTimeout(async () => {
          debounceRef.current = null;
          const target = pendingLevelRef.current;
          pendingLevelRef.current = null;
          if (target == null) {
            resolve();
            return;
          }
          const r = await volumeApi.setVolume({ level: target });
          if (r.ok) {
            apply(r.data);
          } else if (lastConfirmedRef.current) {
            // Revert the optimistic update on error so the slider
            // doesn't lie about the actual sink state.
            setState(lastConfirmedRef.current);
          }
          resolve();
        }, DEBOUNCE_MS);
      });
    },
    [apply],
  );

  const setMuted = useCallback(
    async (m: boolean) => {
      setState((prev) => (prev ? { ...prev, muted: m } : { level: 0, muted: m }));
      const r = await volumeApi.setVolume({ muted: m });
      if (r.ok) {
        apply(r.data);
      } else if (lastConfirmedRef.current) {
        setState(lastConfirmedRef.current);
      }
    },
    [apply],
  );

  return { available, state, setLevel, setMuted, refresh };
}
